import * as Sentry from "@sentry/react";

import { Paragraph, StyledAlert } from "App/global-style";
import { Form, FormikProps } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useErrorTexts, useProductTexts } from "Store/texts/hooks";

import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { AlertType } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton/RadioButtonTrueFalse";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { isValid } from "date-fns";
import { formatToDDMMYYYY } from "Formatting/formatDate";
import { useCurrentCart } from "Store/cart";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { isChild, isInfant } from "Utils/ageUtils";
import { reverseAndPadDDMMYYYY } from "Utils/dateUtils";
import { getError } from "Utils/productUtils";
import { replacePlaceholder } from "Utils/replacePlaceholder";
import { CHILD_ABOUT_STEP } from "..";
import { CHILD_MAX_AGE } from "../childConstants";
import { ChildProductDetailsFormValues } from "./childProductDetailsInterfaces";
import { getChildProductDetailsInitialFormValues } from "./childProductDetailsUtils";
import { validationSchema } from "./childProductDetailsValidation";
import { CloseToMaxAgeWarning } from "./CloseToMaxAgeWarning";

interface Props {
  readonly updatePriceRequest: (
    formValues: ChildProductDetailsFormValues,
  ) => void;
}

export const ChildProductDetails = ({
  updatePriceRequest,
}: Props): React.ReactElement => {
  const cart = useCurrentCart();
  const initialFormState = useFormState<ChildProductDetailsFormValues>(
    CHILD_ABOUT_STEP.id,
    getChildProductDetailsInitialFormValues(cart),
  );
  const [currentDate, setCurrentDate] = useState("");
  const [{ parentGuardianErrorTitle }] = useErrorTexts();

  const [
    {
      formText: { aboutChild },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CHILD));
  const {
    priceCalculation: { priceCalculationResults },
  } = useSelector((state: ReduxState) => state);

  const isNotChildError = (childBirthDate?: string): string => {
    const birthDate = new Date(reverseAndPadDDMMYYYY(childBirthDate));
    if (isValid(birthDate)) {
      if (isInfant(birthDate)) {
        return `${aboutChild.birthDate.infant[lang]}`;
      }
      if (!isChild(birthDate, CHILD_MAX_AGE)) {
        return `${replacePlaceholder(aboutChild.birthDate.adultNew[lang], {
          maxAge: String(CHILD_MAX_AGE),
        })}`;
      }
    }
    return "";
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.CHILD}`,
        );
      }}
    >
      <ProductDetailsTile
        header={CHILD_ABOUT_STEP.title}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={updatePriceRequest}
      >
        {(formProps: FormikProps<ChildProductDetailsFormValues>) => {
          const childError = isNotChildError(currentDate);
          return (
            <Form>
              <Paragraph>{aboutChild.about[lang]}</Paragraph>
              <Input
                large
                id="childFirstName"
                name="childFirstName"
                label={aboutChild.firstName.label[lang]}
                hint={aboutChild.firstName.hint[lang]}
                error={getError("childFirstName", formProps)}
                inputProps={{ maxLength: 25 }}
                inputMode={InputMode.TEXT}
              />
              <Input
                large
                id="childBirthDate"
                name="childBirthDate"
                label={aboutChild.birthDate.label[lang]}
                hint={aboutChild.birthDate.hint[lang]}
                error={childError ? "" : getError("childBirthDate", formProps)}
                placeholder={aboutChild.birthDate.placeholder[lang]}
                onChange={(e) => {
                  e.target.value = formatToDDMMYYYY(e.target.value);
                }}
                inputProps={{
                  maxLength: 10,
                  autoComplete: "off",
                  onBlur: () => {
                    setCurrentDate(
                      formProps.values.childBirthDate
                        ? formProps.values.childBirthDate
                        : "",
                    );
                  },
                }}
                inputMode={InputMode.NUMERIC}
              />
              <CloseToMaxAgeWarning />
              {/*  TODO: link til spedbarnsforsikring */}
              {childError && (
                <StyledAlert
                  alertId="childError"
                  title={aboutChild.birthDate.alertTitle[lang]}
                  description={childError}
                  fluid
                />
              )}
              <RadioButtonTrueFalse
                direction="vertical"
                name="parentGuardian"
                id="parentGuardian"
                hint={aboutChild.parentGuardian.hint[lang]}
                label={aboutChild.parentGuardian.label[lang]}
                error={getError("parentGuardian", formProps)}
                options={[
                  {
                    id: "parentGuardian-yes",
                    label: `${aboutChild.parentGuardian.options.yes[lang]}`,
                    value: true,
                  },
                  {
                    id: "parentGuardian-no",
                    label: `${aboutChild.parentGuardian.options.no[lang]}`,
                    value: false,
                  },
                ]}
              />
              {formProps.values.parentGuardian === BooleanFormType.FALSE && (
                <StyledAlert
                  type="error"
                  alertId="parentGuardian"
                  alertType={AlertType.STOPPER_ALERT}
                  title={parentGuardianErrorTitle[lang]}
                  description={aboutChild.parentGuardian.error[lang]}
                  fluid
                />
              )}
              <StyledAlert title={aboutChild.edh[lang]} />
              {priceCalculationResults.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <ScrollToFirstError />
              <NavigationButtons
                disableNextButton={
                  formProps.values.parentGuardian === BooleanFormType.FALSE
                }
                isLoading={priceCalculationResults.isLoading}
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.CHILD}
                stepId={CHILD_ABOUT_STEP.id}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};
